'use client'

import * as React from 'react'
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

type ComboboxOption = {
  value: string
  label: string
  description?: string
  keywords?: readonly string[]
  disabled?: boolean
}

type ComboboxProps = {
  options: readonly ComboboxOption[]
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  loading?: boolean
  loadingText?: string
  error?: React.ReactNode
  disabled?: boolean
  required?: boolean
  id?: string
  className?: string
  shouldFilter?: boolean
  onSearchChange?: (query: string) => void
  renderOption?: (option: ComboboxOption) => React.ReactNode
  'aria-label': string
  'aria-describedby'?: string
  'aria-invalid'?: React.AriaAttributes['aria-invalid']
}

function defaultFilter(option: ComboboxOption, query: string) {
  const normalizedQuery = query.trim().toLocaleLowerCase()
  if (!normalizedQuery) return true

  return [option.label, option.description, ...(option.keywords ?? [])]
    .filter(Boolean)
    .some((part) => part!.toLocaleLowerCase().includes(normalizedQuery))
}

function Combobox({
  options,
  value,
  onValueChange,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search',
  emptyText = 'No matching options.',
  loading = false,
  loadingText = 'Loading options...',
  error,
  disabled = false,
  required = false,
  id,
  className,
  shouldFilter = true,
  onSearchChange,
  renderOption,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const [activeIndex, setActiveIndex] = React.useState(-1)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const generatedId = React.useId()
  const listboxId = `${generatedId}-listbox`
  const selectedOption = options.find((option) => option.value === value)

  const filteredOptions = React.useMemo(
    () => (shouldFilter ? options.filter((option) => defaultFilter(option, query)) : [...options]),
    [options, query, shouldFilter],
  )

  const optionId = React.useCallback((index: number) => `${listboxId}-option-${index}`, [listboxId])

  React.useEffect(() => {
    if (!open) return

    const selectedIndex = filteredOptions.findIndex(
      (option) => option.value === value && !option.disabled,
    )
    const firstEnabledIndex = filteredOptions.findIndex((option) => !option.disabled)
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : firstEnabledIndex)
  }, [filteredOptions, open, value])

  React.useEffect(() => {
    if (!open || activeIndex < 0) return
    document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, open, optionId])

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
    if (!nextOpen) {
      setQuery('')
      setActiveIndex(-1)
      onSearchChange?.('')
    }
  }

  function handleQueryChange(nextQuery: string) {
    setQuery(nextQuery)
    onSearchChange?.(nextQuery)
  }

  function moveActive(delta: 1 | -1) {
    const enabledIndexes = filteredOptions.flatMap((option, index) =>
      option.disabled ? [] : [index],
    )
    if (enabledIndexes.length === 0) {
      setActiveIndex(-1)
      return
    }

    const currentPosition = enabledIndexes.indexOf(activeIndex)
    const nextPosition =
      currentPosition < 0
        ? delta > 0
          ? 0
          : enabledIndexes.length - 1
        : (currentPosition + delta + enabledIndexes.length) % enabledIndexes.length
    setActiveIndex(enabledIndexes[nextPosition])
  }

  function chooseOption(option: ComboboxOption) {
    if (option.disabled) return
    onValueChange(option.value)
    handleOpenChange(false)
  }

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        moveActive(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        moveActive(-1)
        break
      case 'Home': {
        const firstEnabledIndex = filteredOptions.findIndex((option) => !option.disabled)
        if (firstEnabledIndex >= 0) {
          event.preventDefault()
          setActiveIndex(firstEnabledIndex)
        }
        break
      }
      case 'End': {
        const lastEnabledIndex = filteredOptions.findLastIndex((option) => !option.disabled)
        if (lastEnabledIndex >= 0) {
          event.preventDefault()
          setActiveIndex(lastEnabledIndex)
        }
        break
      }
      case 'Enter': {
        const activeOption = filteredOptions[activeIndex]
        if (activeOption && !activeOption.disabled) {
          event.preventDefault()
          chooseOption(activeOption)
        }
        break
      }
      case 'Escape':
        event.preventDefault()
        handleOpenChange(false)
        break
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="field"
          role="combobox"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          aria-required={required}
          disabled={disabled}
          data-slot="combobox-trigger"
          className={cn('w-full justify-between', className)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault()
              setOpen(true)
            }
          }}
        >
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-left',
              !selectedOption && 'text-muted-foreground',
            )}
          >
            {selectedOption?.label ?? placeholder}
          </span>
          <ChevronsUpDownIcon data-icon="inline-end" aria-hidden="true" className="opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        data-slot="combobox-content"
        className="w-[var(--radix-popover-trigger-width)] p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          inputRef.current?.focus()
        }}
      >
        <div className="border-b p-2">
          <Input
            ref={inputRef}
            role="combobox"
            aria-label={`Search ${ariaLabel}`}
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
            value={query}
            placeholder={searchPlaceholder}
            autoComplete="off"
            onChange={(event) => handleQueryChange(event.target.value)}
            onKeyDown={handleInputKeyDown}
          />
        </div>

        <div
          id={listboxId}
          role="listbox"
          aria-label={`${ariaLabel} options`}
          aria-busy={loading}
          data-slot="combobox-list"
          className="max-h-72 overflow-y-auto overscroll-contain p-1"
        >
          {loading ? (
            <div role="status" className="px-3 py-6 text-center text-sm text-muted-foreground">
              {loadingText}
            </div>
          ) : error ? (
            <div role="alert" className="px-3 py-6 text-center text-sm text-destructive">
              {error}
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyText}</div>
          ) : (
            filteredOptions.map((option, index) => {
              const selected = option.value === value
              const active = index === activeIndex

              return (
                <div
                  key={option.value}
                  id={optionId(index)}
                  role="option"
                  aria-selected={selected}
                  aria-disabled={option.disabled || undefined}
                  data-active={active || undefined}
                  data-disabled={option.disabled || undefined}
                  data-slot="combobox-option"
                  className={cn(
                    'relative flex min-h-9 cursor-default items-start gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none',
                    'data-[active=true]:bg-accent data-[active=true]:text-accent-foreground',
                    'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
                  )}
                  onPointerMove={() => {
                    if (!option.disabled) setActiveIndex(index)
                  }}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => chooseOption(option)}
                >
                  <CheckIcon
                    aria-hidden="true"
                    className={cn('mt-0.5 shrink-0', selected ? 'opacity-100' : 'opacity-0')}
                  />
                  <span className="min-w-0 flex-1">
                    {renderOption ? (
                      renderOption(option)
                    ) : (
                      <>
                        <span className="block truncate font-medium">{option.label}</span>
                        {option.description && (
                          <span className="block truncate text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        )}
                      </>
                    )}
                  </span>
                </div>
              )
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { Combobox, type ComboboxOption, type ComboboxProps }
